import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField
} from '@mui/material';

import {
  CheckOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined
} from '@mui/icons-material';

import { rxDialogStyles } from './RxDialogStyles';

import {
  textFieldSx,
  multitextFieldSx,
  actionWrapperSx,
  actionIconSx,
  addIconSx,
  clearIconSx,
  actionSvgSx
} from '../../Styles/InputStyles';

const emptyMedication = {
  name: '',
  dosage: '',
  directions: '',
  quantity: '',
  refill: ''
};

const MedicationsAdd = ({
  medications,
  onAdd,
  onEdit,
  onDelete,
  comments,
  onCommentsChange
}) => {
  const [form, setForm] = useState(emptyMedication);

  const handleChange = field => event => {
    setForm(previous => ({
      ...previous,
      [field]: event.target.value
    }));
  };

  const handleClear = () => {
    setForm(emptyMedication);
  };

  const handleAdd = () => {
    const name = form.name.trim();

    if (!name) return;

    onAdd({
      ...form,
      name,
      id: Date.now()
    });

    handleClear();
  };

  const canAdd = Boolean(form.name.trim());

  const hasFormValue = Object.values(form).some(value =>
    String(value).trim()
  );

 return (
  <Box sx={rxDialogStyles.panel}>
    <Box sx={rxDialogStyles.panelHeader}>
      <Box sx={rxDialogStyles.panelTitle}>
        Medications
      </Box>
    </Box>

    <Box sx={rxDialogStyles.medicationsScrollArea}>
      <TableContainer sx={rxDialogStyles.medicationsTableContainer}>
        <Table size="small" sx={rxDialogStyles.table}>
          <colgroup>
            <col style={{ width: '38%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: 90 }} />
          </colgroup>

          <TableBody>
            <TableRow sx={rxDialogStyles.inputRow}>
              <TableCell sx={rxDialogStyles.inputCell}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Medication"
                  value={form.name}
                  onChange={handleChange('name')}
                  sx={textFieldSx}
                />
              </TableCell>

              <TableCell sx={rxDialogStyles.inputCell}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Dose"
                  value={form.dosage}
                  onChange={handleChange('dosage')}
                  sx={textFieldSx}
                />
              </TableCell>

              <TableCell sx={rxDialogStyles.inputCell}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Direction"
                  value={form.directions}
                  onChange={handleChange('directions')}
                  sx={textFieldSx}
                />
              </TableCell>

              <TableCell sx={rxDialogStyles.inputCell}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Quantity"
                  value={form.quantity}
                  onChange={handleChange('quantity')}
                  sx={textFieldSx}
                />
              </TableCell>

              <TableCell sx={rxDialogStyles.inputCell}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Refill"
                  value={form.refill}
                  onChange={handleChange('refill')}
                  sx={textFieldSx}
                />
              </TableCell>

              <TableCell
                align="center"
                sx={rxDialogStyles.actionCell}
              >
                <Box sx={actionWrapperSx}>
                  <IconButton
                    size="small"
                    aria-label="Add medication"
                    disabled={!canAdd}
                    onClick={handleAdd}
                    sx={{
                      ...actionIconSx,
                      ...addIconSx
                    }}
                  >
                    <CheckOutlined sx={actionSvgSx} />
                  </IconButton>

                  <IconButton
                    size="small"
                    aria-label="Clear medication"
                    disabled={!hasFormValue}
                    onClick={handleClear}
                    sx={{
                      ...actionIconSx,
                      ...clearIconSx
                    }}
                  >
                    <ClearOutlined sx={actionSvgSx} />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>

            {medications.map(medication => (
              <TableRow key={medication.id} hover sx={rxDialogStyles.inputRow}>
                <TableCell>
                  {medication.name}
                </TableCell>

                <TableCell align="center">
                  {medication.dosage}
                </TableCell>

                <TableCell align="center">
                  {medication.directions}
                </TableCell>

                <TableCell align="center">
                  {medication.quantity}
                </TableCell>

                <TableCell align="center">
                  {medication.refill}
                </TableCell>

                <TableCell
                  align="center"
                  sx={rxDialogStyles.actionCell}
                >
                  <Box sx={rxDialogStyles.actionWrapper}>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${medication.name}`}
                      onClick={() => onEdit(medication)}
                      sx={rxDialogStyles.actionButton}
                    >
                      <EditOutlined
                        sx={rxDialogStyles.editIcon}
                      />
                    </IconButton>

                    <IconButton
                      size="small"
                      aria-label={`Delete ${medication.name}`}
                      onClick={() => onDelete(medication.id)}
                      sx={rxDialogStyles.actionButton}
                    >
                      <DeleteOutlined
                        sx={rxDialogStyles.deleteIcon}
                      />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={rxDialogStyles.commentsWrapper}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Comments"
          value={comments}
          onChange={event =>
            onCommentsChange(event.target.value)
          }
          sx={multitextFieldSx}
        />
      </Box>
    </Box>
  </Box>
);
};

MedicationsAdd.propTypes = {
  medications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]).isRequired,
      name: PropTypes.string.isRequired,
      dosage: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      directions: PropTypes.string,
      quantity: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      refill: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ])
    })
  ),
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  comments: PropTypes.string,
  onCommentsChange: PropTypes.func
};

MedicationsAdd.defaultProps = {
  medications: [],
  onAdd: () => {},
  onEdit: () => {},
  onDelete: () => {},
  comments: '',
  onCommentsChange: () => {}
};

export default MedicationsAdd;